import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CampaignIcon } from '../../assets';
import { MIGRATE_PIECES } from '../../graphql/campaign/mutation';
import { LIST_CAMPAIGN_PIECES } from '../../graphql/pieces/query';
import type { SinglePiece } from '../../types';
import type { ResMigratePieceType } from '../../types/action';
import type { ListCampaignPiecesRes } from '../../types/pieces';
import { Button } from '../common/Button';
import { Error } from '../common/Error';
import { StatusText } from '../common/StatusText';
import { SocialNetworkImage } from '../images/SocialNetworkImage';
import { UsersImages } from '../images/UserImages';
import { CampaignDropdown } from '../inputs/CampaignDropdown';
import { PopupLayout } from './PopupLayout';

type TMigratePieces = {
  pieces: string[];
  campaignId: string;
};

export function MigratePiecesPopup({
  setOpen,
  refetch,
  campaignId,
}: {
  setOpen: any;
  refetch: any;
  campaignId: string;
}) {
  const [pieces, setPieces] = useState<SinglePiece[]>([]);
  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<TMigratePieces>({
    defaultValues: {
      pieces: [],
    },
  });
  const { t } = useTranslation('common');

  const { pieces: selectedPieces } = watch();

  const { loading: loadingQuery, error } = useQuery<ListCampaignPiecesRes>(
    LIST_CAMPAIGN_PIECES,
    {
      variables: {
        campaign_id: campaignId,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: (res: ListCampaignPiecesRes) => {
        setPieces(res.listCampaignPieces.data || []);
      },
    },
  );

  const [migratePieces, { loading }] =
    useMutation<ResMigratePieceType>(MIGRATE_PIECES);

  const onSubmit = (data: TMigratePieces) => {
    if (!data.pieces.length) {
      setError('pieces', {
        message: t('singleCampaign.migrateActionPopup.minimumPieces'),
      });
      return;
    }

    migratePieces({
      variables: {
        pieces: data.pieces,
        campaignId: data.campaignId,
      },
      onCompleted: (res) => {
        reset();
        refetch();
        setOpen(false);
      },
      onError: () => {},
    });
  };

  const closeModal = () => {
    reset();
    setOpen(false);
  };

  const stopPropagationAndSubmit = (event: any) => {
    clearErrors();
    event.stopPropagation();
    handleSubmit(onSubmit)(event);
  };

  const onSelectCheckbox = (pieceId: string) => {
    const newActions = selectedPieces.includes(pieceId)
      ? selectedPieces.filter((id) => id !== pieceId) // si ya está, lo elimina
      : [...selectedPieces, pieceId]; // si no está, lo añade

    setValue('pieces', newActions);
  };

  return (
    <>
      <PopupLayout
        title={t('singleCampaign.migrateActionPopup.title')}
        closeModal={closeModal}
        icon={<CampaignIcon />}
      >
        <form
          onSubmit={stopPropagationAndSubmit}
          className="flex flex-col gap-y-5 mt-5"
        >
          <div className="flex flex-col gap-y-4 ">
            <div className="flex flex-col gap-y-4 max-h-[60vh] overflow-y-auto customScroll">
              {pieces.map((p) => (
                <div className="flex flex-row gap-x-3 items-center justify-center">
                  <input
                    type="checkbox"
                    id={p.piece_id}
                    checked={selectedPieces.includes(p.piece_id)}
                    onChange={() => onSelectCheckbox(p.piece_id)}
                    className="checkbox flex size-[18px] items-center justify-center rounded-md border bg-white  focus:ring-0 focus:ring-transparent focus:ring-offset-0"
                  />
                  <label
                    htmlFor={p.piece_id}
                    className="gap-x-1 grid grid-cols-[30px_2fr_2fr_2fr] w-full"
                  >
                    <UsersImages
                      userCount={1}
                      userImages={[p.profile_picture]}
                      influencers
                      size="size-[24px]"
                    />
                    <span className="truncate">{p.user_name}</span>
                    <div className="flex flex-row gap-x-1 items-center justify-center">
                      <SocialNetworkImage
                        socialNetwork={p.platform as any}
                        size="size-[20px]"
                      />
                      <span className="text-center">
                        {t(`actionType.${p.type}`)}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      <StatusText status={p.status as any} type="action" />
                    </div>
                  </label>
                </div>
              ))}
            </div>
            {errors.pieces?.message && <Error error={errors.pieces?.message} />}

            <CampaignDropdown
              label={t('singleCampaign.migrateActionPopup.selectCampaign')}
              validation={{
                required: t(
                  'singleCampaign.migrateActionPopup.campaignRequired',
                ),
              }}
              error={errors.campaignId?.message}
              control={control}
              name="campaignId"
            />
          </div>
          <div className="flex justify-end">
            <div className="w-[150px]">
              <Button
                style="light-blue"
                id="createBrandPopup"
                text={t('global.save')}
                customClassnames="w-full justify-center"
                type="submit"
                loading={loading}
              />
            </div>
          </div>
        </form>
      </PopupLayout>
    </>
  );
}
